import { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertCircle, AlertTriangle, Bug, Cpu, Database, Settings, Timer, Trash, Undo2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useDeveloperMode } from '@flows.cash/core';
import { useTranslation } from 'react-i18next';

const DevToolsSettings = () => {
  const { t } = useTranslation();
  const { isDeveloper, isLoading } = useDeveloperMode();
  const [activeTab, setActiveTab] = useState('info');
  const [sqlQuery, setSqlQuery] = useState('');
  const [queryResult, setQueryResult] = useState<any>(null);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [networkLogs, setNetworkLogs] = useState<any[]>([]);
  const [isSimulatingError, setIsSimulatingError] = useState(false);
  const [errorType, setErrorType] = useState('network');
  const [slowResponseEnabled, setSlowResponseEnabled] = useState(false);
  const [slowResponseTime, setSlowResponseTime] = useState(2000);
  
  // System Info
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const language = window.navigator.language;
  const memory = (window.navigator as any).deviceMemory;
  const online = window.navigator.onLine;
  const cores = window.navigator.hardwareConcurrency;
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isDeveloper) {
    return (
      <Alert variant="destructive" className="my-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          {t('settings.developerTools.accessDenied')}
        </AlertDescription>
      </Alert>
    );
  }

  // Execute SQL query
  const executeQuery = async () => {
    if (!sqlQuery.trim()) {
      toast.error('Please enter a valid SQL query');
      return;
    }
    
    try {
      setQueryError(null);
      // Only allow select queries for safety
      if (!sqlQuery.toLowerCase().trim().startsWith('select')) {
        throw new Error('Only SELECT queries are allowed for security reasons');
      }
      
      const { data, error } = await supabase.rpc('execute_developer_query', { query: sqlQuery });
      
      if (error) throw error;
      
      setQueryResult(data);
      toast.success('Query executed successfully');
    } catch (error) {
      console.error('Query execution error:', error);
      setQueryError(error instanceof Error ? error.message : 'Failed to execute query');
      toast.error('Failed to execute query');
    }
  };
  
  // Simulate errors
  const simulateError = () => {
    setIsSimulatingError(true);
    
    switch (errorType) {
      case 'network':
        toast.error('Simulated network error');
        // Simulate a network error
        window.dispatchEvent(new CustomEvent('flows:dev:simulate-network-error'));
        break;
      case 'api':
        toast.error('Simulated API error');
        // Simulate an API error
        window.dispatchEvent(new CustomEvent('flows:dev:simulate-api-error'));
        break;
      case 'js':
        toast.error('Simulated JavaScript error');
        // Simulate a JavaScript error
        setTimeout(() => {
          try {
            // @ts-ignore - Intentionally cause an error
            const error = nonExistentFunction();
          } catch (e) {
            console.error('Simulated JS Error:', e);
          }
        }, 100);
        break;
      case 'memory':
        toast.error('Simulated memory leak');
        // Simulate memory leak
        window.dispatchEvent(new CustomEvent('flows:dev:simulate-memory-leak'));
        break;
      default:
        break;
    }
    
    setTimeout(() => {
      setIsSimulatingError(false);
    }, 2000);
  };
  
  // Clear network logs
  const clearNetworkLogs = () => {
    setNetworkLogs([]);
    toast.success('Network logs cleared');
  };
  
  // Simulate slow response
  const toggleSlowResponse = () => {
    const newState = !slowResponseEnabled;
    setSlowResponseEnabled(newState);
    
    window.dispatchEvent(new CustomEvent('flows:dev:toggle-slow-response', {
      detail: {
        enabled: newState,
        delay: slowResponseTime
      }
    }));
    
    toast.success(`Slow response simulation ${newState ? 'enabled' : 'disabled'}`);
  };
  
  // Reset app state
  const resetAppState = () => {
    if (confirm('Are you sure you want to reset the app state? This will clear all cached data and reload the page.')) {
      // Clear local storage and IndexedDB
      localStorage.clear();
      
      // Clear IndexedDB stores
      window.indexedDB.databases().then(databases => {
        databases.forEach(db => {
          if (db.name) {
            window.indexedDB.deleteDatabase(db.name);
          }
        });
      });
      
      toast.success('App state reset, reloading...');
      
      // Give toast time to show before reload
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };
  
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center space-x-2">
        <Settings className="h-5 w-5 text-primary" />
        <h1 className="text-2xl font-bold">{t('settings.developerTools.title')}</h1>
        <Badge className="ml-2" variant="outline">Beta</Badge>
      </div>
      
      <p className="text-muted-foreground">
        {t('settings.developerTools.description')}
      </p>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-5 mb-6">
          <TabsTrigger value="info">{t('settings.developerTools.systemInfo')}</TabsTrigger>
          <TabsTrigger value="database">{t('settings.developerTools.database')}</TabsTrigger>
          <TabsTrigger value="network">{t('settings.developerTools.network')}</TabsTrigger>
          <TabsTrigger value="errors">{t('settings.developerTools.errors')}</TabsTrigger>
          <TabsTrigger value="performance">{t('settings.developerTools.performance')}</TabsTrigger>
        </TabsList>
        
        {/* System Info Tab */}
        <TabsContent value="info" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.developerTools.systemInfo')}</CardTitle>
              <CardDescription>{t('settings.developerTools.systemInfoDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground">Browser</h3>
                  <p className="text-sm">{userAgent.split(' ').slice(-3).join(' ')}</p>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground">Platform</h3>
                  <p className="text-sm">{platform}</p>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground">Language</h3>
                  <p className="text-sm">{language}</p>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground">Cores</h3>
                  <p className="text-sm">{cores || 'Unknown'}</p>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground">Memory</h3>
                  <p className="text-sm">{memory ? `${memory}GB` : 'Unknown'}</p>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground">Online Status</h3>
                  <p className="text-sm">
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${online ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    {online ? 'Online' : 'Offline'}
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground">Screen</h3>
                  <p className="text-sm">{`${window.screen.width} x ${window.screen.height}`}</p>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground">Pixel Ratio</h3>
                  <p className="text-sm">{window.devicePixelRatio}</p>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button variant="outline" onClick={resetAppState} className="w-full">
                <Undo2 className="mr-2 h-4 w-4" />
                {t('settings.developerTools.resetAppState')}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        {/* Database Tab */}
        <TabsContent value="database" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.developerTools.database')}</CardTitle>
              <CardDescription>{t('settings.developerTools.databaseDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t('settings.developerTools.securityNotice')}</AlertTitle>
                <AlertDescription>
                  {t('settings.developerTools.securityNoticeDesc')}
                </AlertDescription>
              </Alert>
              
              <Textarea 
                placeholder="SELECT * FROM your_table LIMIT 10"
                value={sqlQuery}
                onChange={(e) => setSqlQuery(e.target.value)}
                className="min-h-[120px] font-mono"
              />
              
              {queryError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Query Error</AlertTitle>
                  <AlertDescription>{queryError}</AlertDescription>
                </Alert>
              )}
              
              {queryResult && (
                <div className="border rounded-md overflow-auto">
                  <pre className="p-4 text-xs overflow-auto max-h-[300px]">
                    {JSON.stringify(queryResult, null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => {
                setSqlQuery('');
                setQueryResult(null);
                setQueryError(null);
              }}>
                Clear
              </Button>
              <Button onClick={executeQuery}>
                <Database className="mr-2 h-4 w-4" />
                Execute Query
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        {/* Network Tab */}
        <TabsContent value="network" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.developerTools.network')}</CardTitle>
              <CardDescription>{t('settings.developerTools.networkDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-sm font-medium">Network Logs</h3>
                  <p className="text-sm text-muted-foreground">
                    {networkLogs.length} request{networkLogs.length !== 1 ? 's' : ''} captured
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={clearNetworkLogs}>
                  <Trash className="mr-2 h-4 w-4" />
                  {t('settings.developerTools.clearLogs')}
                </Button>
              </div>
              
              <div className="border rounded-md overflow-hidden">
                {networkLogs.length > 0 ? (
                  <div className="max-h-[300px] overflow-auto">
                    {/* Network logs would be displayed here */}
                    <pre className="p-4 text-xs">No network logs captured yet</pre>
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <p>No network activity captured yet</p>
                    <p className="text-sm mt-2">Network requests will appear here automatically</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Error Simulation Tab */}
        <TabsContent value="errors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.developerTools.errors')}</CardTitle>
              <CardDescription>{t('settings.developerTools.errorsDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <Bug className="h-4 w-4" />
                <AlertTitle>Caution</AlertTitle>
                <AlertDescription>
                  Simulating errors may cause temporary disruption to the application. Use with caution.
                </AlertDescription>
              </Alert>
              
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="error-type">Error Type</Label>
                  <Select 
                    value={errorType} 
                    onValueChange={setErrorType}
                  >
                    <SelectTrigger id="error-type">
                      <SelectValue placeholder="Select error type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="network">Network Error</SelectItem>
                      <SelectItem value="api">API Error</SelectItem>
                      <SelectItem value="js">JavaScript Error</SelectItem>
                      <SelectItem value="memory">Memory Leak</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  variant="destructive" 
                  onClick={simulateError}
                  disabled={isSimulatingError}
                >
                  {isSimulatingError ? 'Simulating...' : 'Simulate Error'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.developerTools.performance')}</CardTitle>
              <CardDescription>{t('settings.developerTools.performanceDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between border-b pb-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Slow Network Simulation</Label>
                  <p className="text-sm text-muted-foreground">
                    Simulate slow network conditions
                  </p>
                </div>
                <Switch 
                  checked={slowResponseEnabled}
                  onCheckedChange={toggleSlowResponse}
                />
              </div>
              
              {slowResponseEnabled && (
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="delay-time">Response Delay (ms)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="delay-time"
                        type="number"
                        min={100}
                        max={10000}
                        value={slowResponseTime}
                        onChange={(e) => setSlowResponseTime(parseInt(e.target.value))}
                      />
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          if (slowResponseEnabled) {
                            window.dispatchEvent(new CustomEvent('flows:dev:toggle-slow-response', {
                              detail: {
                                enabled: true,
                                delay: slowResponseTime
                              }
                            }));
                            toast.success(`Delay updated to ${slowResponseTime}ms`);
                          }
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <Label className="text-base">Performance Metrics</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-md p-4 flex items-center">
                      <div className="mr-4 bg-primary/10 p-2 rounded-full">
                        <Timer className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Page Load</p>
                        <p className="text-2xl font-bold">
                          {performance && 'navigation' in performance 
                            ? Math.round(performance.getEntriesByType('navigation')[0]?.duration || 0) 
                            : 'N/A'
                          }
                          <span className="text-sm font-normal text-muted-foreground ml-1">ms</span>
                        </p>
                      </div>
                    </div>
                    <div className="border rounded-md p-4 flex items-center">
                      <div className="mr-4 bg-primary/10 p-2 rounded-full">
                        <Cpu className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Memory Usage</p>
                        <p className="text-2xl font-bold">
                          {performance && 'memory' in performance 
                            ? Math.round((performance as any).memory?.usedJSHeapSize / (1024 * 1024)) 
                            : 'N/A'
                          }
                          <span className="text-sm font-normal text-muted-foreground ml-1">MB</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  // Force garbage collection if supported
                  if (window.gc) {
                    try {
                      // @ts-ignore
                      window.gc();
                      toast.success('Garbage collection requested');
                    } catch (e) {
                      toast.error('Garbage collection not available');
                    }
                  } else {
                    toast.error('Garbage collection not supported in this browser');
                  }
                }}
              >
                Run Garbage Collection
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DevToolsSettings; 